import { api } from './api.js';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function text(value, fallback = '') {
  if (typeof value !== 'string') return fallback;
  const normalized = value.replace(/\s+/g, ' ').trim().slice(0, 240);
  return normalized || fallback;
}

function positiveInteger(value) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : 0;
}

export function curatedMetaPublishWorkflowId(value) {
  const normalized = text(value);
  return UUID_PATTERN.test(normalized) ? normalized : '';
}

export function curatedMetaPublishScopeKey(companyId, campaignId, workflowJobId) {
  const safeCompanyId = text(companyId);
  const safeCampaignId = text(campaignId);
  const safeWorkflowJobId = curatedMetaPublishWorkflowId(workflowJobId);
  if (!safeCompanyId || !safeCampaignId || !safeWorkflowJobId) return '';
  return JSON.stringify([
    safeCompanyId,
    safeCampaignId,
    safeWorkflowJobId,
  ]);
}

export function curatedMetaPublishSummary(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const ads = Array.isArray(snapshot.ads) ? snapshot.ads : [];
  const renovationTypeCounts = new Map();
  const creativeVersionIds = new Set();
  let exactAds = true;
  for (const ad of ads) {
    const renovationTypeId = text(ad?.renovationTypeId || ad?.renovationTypeKey);
    const creativeVersionId = text(ad?.creativeVersionId);
    if (!renovationTypeId || !creativeVersionId || creativeVersionIds.has(creativeVersionId)) {
      exactAds = false;
      continue;
    }
    creativeVersionIds.add(creativeVersionId);
    renovationTypeCounts.set(
      renovationTypeId,
      (renovationTypeCounts.get(renovationTypeId) || 0) + 1,
    );
  }
  const renovationTypes = new Set(renovationTypeCounts.keys());
  const expectedAdCount = ads.length;
  const campaignCount = positiveInteger(snapshot.structure?.campaignCount);
  const adSetCount = positiveInteger(snapshot.structure?.adSetCount);
  const adCount = positiveInteger(snapshot.structure?.adCount);
  const initialStatus = text(snapshot.structure?.initialStatus).toUpperCase();
  const campaignName = text(snapshot.campaign?.name, 'Curated Meta campaign');
  const adAccount = text(snapshot.meta?.adAccountLabel);
  const instantForm = text(snapshot.meta?.instantFormLabel);
  const facebookPage = text(snapshot.meta?.pageLabel);
  const instagram = text(snapshot.meta?.instagramLabel);
  const monthlyMajor = text(snapshot.budget?.monthlyMajor);
  const currency = text(snapshot.budget?.currency).toUpperCase();
  const validBudgetFormat = /^\d+(?:\.\d{1,2})?$/.test(monthlyMajor);
  const [whole = '0', fraction = ''] = validBudgetFormat ? monthlyMajor.split('.') : [];
  const validBudget = validBudgetFormat
    && ((BigInt(whole) * 100n) + BigInt(fraction.padEnd(2, '0'))) > 0n
    && /^[A-Z]{3}$/.test(currency);
  const exactStructure = campaignCount === 1
    && adSetCount === 1
    && exactAds
    && [...renovationTypeCounts.values()].every((count) => count >= 1 && count <= 2)
    && expectedAdCount > 0
    && adCount === expectedAdCount
    && ads.length === expectedAdCount
    && initialStatus === 'PAUSED';
  if (!exactStructure || !adAccount || !instantForm || !facebookPage || !validBudget) return null;
  return Object.freeze({
    campaignName,
    campaignCount,
    adSetCount,
    adCount,
    renovationTypeCount: renovationTypes.size,
    adAccount,
    instantForm,
    facebookPage,
    instagram,
    monthlyMajor,
    currency,
    initialStatus,
  });
}

export function curatedMetaPublishConfirmation(snapshot, { retry = false } = {}) {
  const summary = curatedMetaPublishSummary(snapshot);
  if (!summary) return '';
  return [
    retry
      ? 'Resume creating the incomplete campaign in Meta?'
      : 'Create this approved campaign in Meta?',
    '',
    `Campaign: ${summary.campaignName}`,
    `Structure: ${summary.campaignCount} campaign, ${summary.adSetCount} shared ad set, ${summary.adCount} approved ads across ${summary.renovationTypeCount} renovation type${summary.renovationTypeCount === 1 ? '' : 's'}`,
    `Ad account: ${summary.adAccount}`,
    `Instant Form: ${summary.instantForm}`,
    `Facebook Page: ${summary.facebookPage}`,
    `Instagram identity: ${summary.instagram || 'Facebook Page identity only'}`,
    `Monthly campaign budget: ${summary.monthlyMajor} ${summary.currency}`,
    '',
    retry
      ? 'BeyondLeads will reuse the existing publish attempt and create only missing Meta objects.'
      : 'BeyondLeads will create the approved objects only after you confirm.',
    'Every campaign, ad set, and ad will be created PAUSED. PAUSED objects cannot spend until an owner or admin activates the campaign separately.',
  ].join('\n');
}

export function curatedMetaPublishSnapshotFromDetail(detail) {
  const campaign = detail?.campaign || {};
  const configuration = detail?.configuration || {};
  const meta = configuration.meta || {};
  return {
    campaign: {
      id: text(campaign.id),
      name: text(campaign.name),
    },
    meta: {
      adAccountLabel: text(meta.adAccount?.label || meta.adAccountLabel),
      instantFormLabel: text(meta.instantForm?.label || meta.instantFormLabel),
      pageLabel: text(meta.instantForm?.owningPage?.label || meta.pageLabel),
      instagramLabel: text(meta.instagramIdentity?.label || meta.instagramLabel),
    },
    budget: {
      monthlyMajor: text(configuration.budget?.monthlyMajor),
      currency: text(configuration.budget?.currency).toUpperCase(),
    },
    structure: {
      campaignCount: configuration.structure?.campaignCount,
      adSetCount: configuration.structure?.adSetCount,
      adCount: configuration.structure?.adCount,
      initialStatus: text(configuration.structure?.initialStatus, 'PAUSED'),
    },
    ads: Array.isArray(configuration.ads) ? configuration.ads : [],
  };
}

export async function executeCuratedMetaPublish({
  requestApi = api,
  companyId,
  workflowJobId,
} = {}) {
  const safeCompanyId = text(companyId);
  const safeWorkflowJobId = curatedMetaPublishWorkflowId(workflowJobId);
  if (!safeCompanyId || !safeWorkflowJobId) throw new Error('curated_campaign_publish_binding_unavailable');
  return requestApi.post(
    `/api/saas/workflows/${encodeURIComponent(safeWorkflowJobId)}/publish-intents/meta/execute`,
    {},
    { headers: { 'x-company-id': safeCompanyId } },
  );
}

export function curatedMetaPublishRecovery(status, workflowJobId) {
  const normalizedStatus = text(status).toLowerCase();
  const bound = Boolean(curatedMetaPublishWorkflowId(workflowJobId));
  return Object.freeze({
    canCreate: bound && normalizedStatus === 'approved',
    canRetry: bound && ['partial', 'failed'].includes(normalizedStatus),
    isPublishing: normalizedStatus === 'publishing',
    isPaused: normalizedStatus === 'paused',
    operational: ['publishing', 'paused', 'partial', 'failed'].includes(normalizedStatus),
  });
}

// Resume through the same campaign-scoped authorization used by the final
// Review action. The configuration read is local and cannot start execution.
export async function resumeCuratedPausedCreation({ companyId, campaignId, workflowJobId }, client = api) {
  if (!text(companyId) || ![campaignId, workflowJobId].every((id) => UUID_PATTERN.test(id || ''))) {
    throw new Error('Refresh the campaign before retrying.');
  }
  const endpoint = `/api/saas/campaigns/${encodeURIComponent(campaignId)}/curated-configuration`;
  const headers = { headers: { 'x-company-id': companyId } };
  const current = await client.get(endpoint, headers);
  const review = current?.creationReview;
  if (!review?.ready || current?.campaign?.id !== campaignId || current?.approval?.workflowJobId !== workflowJobId) {
    throw new Error('Campaign creation changed. Refresh before retrying.');
  }
  return client.post(`${endpoint}/create-paused`, {
    expectedRevision: review.expectedRevision, expectedReviewHash: review.reviewHash,
    idempotencyKey: crypto.randomUUID(),
  }, headers);
}
